// fitRecordStore.js
//
// WHY THIS EXISTS
// ────────────────────────────────────────────────────────────────────────
// Same module-outside-the-component-tree pattern as fittingSessionStore.js
// and formSessionStore.js, for the same reason: DataFittingPage unmounts
// on route change, plain useState would die with it. This store holds:
//   - the current reaction_system_id (fetched once via
//     GET /api/reaction-systems/default when the page loads for a project)
//   - the list of saved fits for that system (for the Compare Fits table
//     inside the Optimize tab)
//   - which fit_uuids are currently checked for comparison
//   - the last compare result returned by the backend
//
// This does NOT duplicate fittingSessionStore.js's job. That store still
// owns "is a run in progress / what did the run I just kicked off return".
// This store owns "what's been explicitly saved to history, and what's
// selected for comparison" — a saved fit only enters this store via the
// explicit saveFitRecord() call below (mirroring Madhura's "Save this fit"
// button, not an automatic side effect of every run).

// API endpoint resolution without a separate apiConfig file.
// Local CRA development uses the existing Flask backend on :5000.
// Production can override with REACT_APP_API_ORIGIN; otherwise same-origin /api is used.
const API_ORIGIN = (process.env.REACT_APP_API_ORIGIN ||
  ((typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
    ? 'http://localhost:5000'
    : (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5000'))).replace(/\/$/, '');
const API_BASE = `${API_ORIGIN}/api`;

function authHeaders() {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : '';
  return token ? { Authorization: token } : {};
}

// BUG FIX (Shankar, 25-Aug): a missing/expired token made every call in this
// store fail with 401, and each caller handled that the same wrong way —
// stuff the raw "<thing> failed: 401" string into state.error and leave the
// user sitting on the page with nothing usable (Compare Fits: "resolve
// default project failed: 401", full stop — no path back to a working
// session). A 401 here doesn't mean "this one request failed," it means
// the session itself is over, so it's handled once, centrally, the same
// way DataFittingPage's own idle-session-timeout already does it
// (see DataFittingPage.jsx's handleSessionLogout): drop the stale token
// and send the user to /login. Every function below that hits the API
// checks for 401 via this helper before falling through to its normal
// error handling, so a real 4xx/5xx (not an auth problem) still surfaces
// through state.error as before.
function handleUnauthorized() {
  if (typeof localStorage !== 'undefined') localStorage.removeItem('token');
  // Login lives at '/' (App.js) — not '/login', which isn't a registered route.
  if (typeof window !== 'undefined') window.location.href = '/';
}

let state = {
  reactionSystemId: null,   // system_uuid, once resolved for the current project
  reactionSystemName: null,
  // BUG FIX: this used to be resolved exactly once (see ensureReactionSystem
  // below) and never re-checked, so it kept pointing at whichever system
  // happened to be current when the page first mounted, even after the user
  // switched to a genuinely different reaction system. lastSignatureKey
  // remembers what species/reactions/reactor config the cached
  // reactionSystemId was resolved FOR, so a change can be detected and
  // re-resolved against (see ensureReactionSystem).
  lastSignatureKey: null,
  fits: [],                 // summary rows for the Compare Fits table
  selectedFitUuids: [],     // checkbox selection, max 4 (MAX_COMPARE server-side)
  compareResult: null,      // last /fit-records/compare response
  // Runs across ALL of the user's reaction systems (Dashboard + Run History).
  // `fits` above stays scoped to the current system because the Optimize tab's
  // compare panel needs exactly that. allFitsScope tells the UI what it got:
  // 'all' = backend honoured GET /fit-records?scope=all, 'current' = fell back.
  allFits: [],
  allFitsScope: 'current',
  // Set by stageRestore() ("Open in Optimize"); DataFittingPage reads it once
  // on mount via consumePendingRestore() and refills the form from it.
  pendingRestore: null,
  loading: false,
  error: null,
};

// Last form state seen by autosaveFormState(). saveFitRecord() attaches it to
// the saved run as `input_snapshot`, so every saved run carries the inputs it
// was produced from (client item #23), not just the optimizer output.
let lastFormState = null;

const listeners = new Set();

function notify() {
  listeners.forEach((fn) => {
    try { fn(state); } catch (e) { /* one bad listener shouldn't break the rest */ }
  });
}

function setState(patch) {
  state = { ...state, ...(typeof patch === 'function' ? patch(state) : patch) };
  notify();
  return state;
}

export function getFitRecordState() {
  return state;
}

export function subscribeFitRecords(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Call this from the app's logout handler (Navbar.jsx / wherever logout
// lives). This store is a plain module-level singleton, so it survives
// client-side route navigation on purpose (that's what fixes the "run
// still going in the background" and "lost my saved fits" bugs) — but
// that also means, without this, a second user logging into the same tab
// without a full page reload would still see the first user's cached
// reactionSystemId/fits until something happened to overwrite it.
export function resetFitRecordState() {
  state = {
    reactionSystemId: null,
    reactionSystemName: null,
    lastSignatureKey: null,
    fits: [],
    selectedFitUuids: [],
    compareResult: null,
    allFits: [],
    allFitsScope: 'current',
    pendingRestore: null,
    loading: false,
    error: null,
  };
  lastFormState = null;
  notify();
}

// ── default project resolution ──────────────────────────────────────────
//
// BUG FIX: this used to cache a self-created project_id in localStorage
// and reuse it forever. If localStorage is ever cleared (e.g. on logout)
// or the browser is later used by a different account, that cache goes
// stale, a brand-new project + reaction_system get created next time,
// and every previously-saved fit becomes unreachable — even though it's
// still in the database, untouched, under the OLD reaction_system_id.
// Fixed by dropping the client-side cache entirely: GET /api/projects/default
// is idempotent and keyed only on the authenticated user (their JWT), so
// it always resolves back to the same project for that account, on any
// browser, regardless of logout/login.
export async function resolveDefaultProjectId() {
  try {
    const res = await fetch(`${API_BASE}/projects/default`, { headers: authHeaders() });
    if (res.status === 401) { handleUnauthorized(); return null; }
    if (!res.ok) throw new Error(`resolve default project failed: ${res.status}`);
    const data = await res.json();
    if (!data.project_id) throw new Error('resolve default project: no project_id in response');
    return data.project_id;
  } catch (e) {
    setState({ error: e.message });
    return null;
  }
}

// ── reaction system resolution ──────────────────────────────────────────
//
// BUG FIX: the old version short-circuited on `if (state.reactionSystemId)
// return state.reactionSystemId;` — meaning it resolved (or auto-created)
// a reaction_systems row exactly ONCE, on first call, and every later call
// (even with a completely different seedPayload) just handed back that
// same cached id without ever asking the backend again. seedPayload was
// also never actually sent — the fetch below never included it — so the
// backend had no content to create/match against in the first place.
//
// Now: seedPayload (reactor_mode, reaction_phase, species, reactions) is
// POSTed on every call, and the cache is only reused when its signature
// matches what it was last resolved for. A caller should pass the
// CURRENT reaction system's signature every time the user's setup could
// plausibly have changed (DataFittingPage does this in a useEffect keyed
// off reactionSystem/reactorMode/reactionPhase/activeSpecies/reactions —
// not just once on mount).
export async function ensureReactionSystem(projectId, seedPayload = {}) {
  const signatureKey = JSON.stringify({ projectId, ...seedPayload });
  if (state.reactionSystemId && state.lastSignatureKey === signatureKey) {
    return state.reactionSystemId;
  }
  setState({ loading: true, error: null });
  try {
    const res = await fetch(`${API_BASE}/reaction-systems/default`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ project_id: projectId, ...seedPayload }),
    });
    let systemUuid, name;
    if (res.status === 401) { handleUnauthorized(); return null; }
    if (res.status === 404) {
      // no project match yet (e.g. brand-new project) — let the caller
      // decide whether to retry after the project actually exists
      setState({ loading: false });
      return null;
    }
    if (res.ok) {
      const data = await res.json();
      systemUuid = data.system_uuid;
      name = data.name;
    } else {
      throw new Error(`reaction-systems/default failed: ${res.status}`);
    }
    // If this resolved to a DIFFERENT system than before (user switched
    // reaction systems), the previously-loaded fit list/selection/compare
    // result belong to the old system — clear them so the UI doesn't show
    // stale data while refreshFitRecords() re-fetches for the new one.
    const switchedSystem = state.reactionSystemId && state.reactionSystemId !== systemUuid;
    setState({
      reactionSystemId: systemUuid, reactionSystemName: name,
      lastSignatureKey: signatureKey, loading: false,
      ...(switchedSystem ? { fits: [], selectedFitUuids: [], compareResult: null } : {}),
    });
    return systemUuid;
  } catch (e) {
    setState({ loading: false, error: e.message });
    return null;
  }
}

export async function renameReactionSystem(newName) {
  if (!state.reactionSystemId) return;
  return renameReactionSystemByUuid(state.reactionSystemId, newName);
}

// Rename ANY of the user's systems, not just the one currently active in this
// session — used by the Systems page (client item #19's left-menu "Systems"
// entry). Updates every list that already has rows for this system in
// memory (fits / allFits / the active session's name) so the new name shows
// up immediately, without waiting on a refetch.
export async function renameReactionSystemByUuid(systemUuid, newName) {
  if (!systemUuid || !newName || !newName.trim()) return false;
  const res = await fetch(`${API_BASE}/reaction-systems/${systemUuid}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ name: newName.trim() }),
  });
  if (res.status === 401) { handleUnauthorized(); return false; }
  if (!res.ok) return false;
  const relabel = (rows) => rows.map((r) => (r.system_uuid === systemUuid ? { ...r, system_name: newName.trim() } : r));
  setState({
    allFits: relabel(state.allFits),
    fits: relabel(state.fits),
    ...(state.reactionSystemId === systemUuid ? { reactionSystemName: newName.trim() } : {}),
  });
  return true;
}

// ── fit history list ────────────────────────────────────────────────────

export async function refreshFitRecords() {
  if (!state.reactionSystemId) return;
  setState({ loading: true, error: null });
  try {
    const res = await fetch(
      `${API_BASE}/fit-records?reaction_system_id=${encodeURIComponent(state.reactionSystemId)}`,
      { headers: authHeaders() }
    );
    if (res.status === 401) { handleUnauthorized(); return; }
    if (!res.ok) throw new Error(`GET fit-records failed: ${res.status}`);
    const fits = await res.json();
    setState({ fits, loading: false });
  } catch (e) {
    setState({ loading: false, error: e.message });
  }
}

// ── runs across every reaction system ───────────────────────────────────
//
// Client item #24: after a methyl-acetate run and then an acetic-acid run,
// BOTH stay visible. refreshFitRecords() above is per-system by design, so
// this is a second, user-wide list.
//
// BACKEND CONTRACT (needs a small change in fit_records_api.py):
//   GET /api/fit-records?scope=all  ->  every fit_records row owned by the
//   authenticated user, same columns as the per-system list PLUS
//   `system_name` and `system_uuid`, and `is_example` when known.
// Until that exists the call fails soft: allFits mirrors the current system
// and allFitsScope stays 'current' so the UI can say so instead of silently
// showing a partial history.
export async function refreshAllFitRecords() {
  const fallback = () => setState({ allFits: state.fits, allFitsScope: 'current' });
  try {
    const res = await fetch(`${API_BASE}/fit-records?scope=all`, { headers: authHeaders() });
    if (res.status === 401) { handleUnauthorized(); return; }
    if (!res.ok) { fallback(); return; }
    const rows = await res.json();
    if (!Array.isArray(rows)) { fallback(); return; }
    setState({ allFits: rows, allFitsScope: 'all' });
  } catch (e) {
    fallback();
  }
}

// One call for Dashboard / Run History: resolve project -> system -> load
// both the per-system and the all-systems lists.
export async function bootstrapFitRecords() {
  const projectId = await resolveDefaultProjectId();
  if (!projectId) return null;
  const systemId = await ensureReactionSystem(projectId);
  if (systemId) {
    await refreshFitRecords();
    await refreshAllFitRecords();
  }
  return systemId;
}

// Pure helper shared by Dashboard and Run History: user's own fits first
// (newest first, capped), example runs shown separately (capped, and NOT
// counted against the user cap). Client item #24. `is_example` missing on a
// row means "own fit", so this is safe before the backend sends the flag.
export function selectVisibleRuns(rows, { maxUser = 10, maxExample = 5 } = {}) {
  const newest = (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0);
  const own = rows.filter((r) => !r.is_example).sort(newest);
  const examples = rows.filter((r) => r.is_example).sort(newest);
  return {
    own: own.slice(0, maxUser),
    examples: examples.slice(0, maxExample),
    ownTotal: own.length,
    exampleTotal: examples.length,
  };
}

// ── reopen a saved run's inputs ─────────────────────────────────────────
// "Open in Optimize" used to only flip is_active. Now it also stages the
// run's saved inputs for DataFittingPage to pick up (client item #23).
export async function stageRestore(fitUuid) {
  const detail = await fetchFitDetail(fitUuid);
  if (!detail) return null;
  const pending = {
    fit_uuid: fitUuid,
    label: detail.label,
    input_snapshot: detail.input_snapshot || null, // null for runs saved before this change
    detail,
  };
  setState({ pendingRestore: pending });
  return pending;
}

export function consumePendingRestore() {
  const pending = state.pendingRestore;
  if (pending) setState({ pendingRestore: null });
  return pending;
}

// payload shape expected by POST /api/fit-records (see fit_records_api.py):
// { system_uuid, label?, notes?, model_type, reversibility, exponent_mode,
//   keq_mode?, lhhw_denom_order?, adsorption_ki?, dataset_selection,
//   fit_scope?, objective_weights?, optimizer_config, fitted_parameters,
//   parameter_bounds?, metrics, status, n_datapoints? }
export async function saveFitRecord(payload) {
  if (!state.reactionSystemId) {
    // BUG FIX: this used to just throw, with nothing ever writing to
    // state.error — so the UI's catch block (which relies on the store's
    // `error` field to display failures) had nothing to show, and the
    // click silently did nothing. Now it's visible.
    const msg = 'no reaction system resolved yet';
    setState({ error: msg });
    throw new Error(msg);
  }
  const res = await fetch(`${API_BASE}/fit-records`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({
      // input_snapshot first so an explicit payload.input_snapshot wins.
      ...(lastFormState ? { input_snapshot: lastFormState } : {}),
      ...payload,
      system_uuid: state.reactionSystemId,
    }),
  });
  if (res.status === 401) { handleUnauthorized(); throw new Error('session expired'); }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const msg = err.error || `save failed: ${res.status}`;
    setState({ error: msg });
    throw new Error(msg);
  }
  const data = await res.json();
  setState({ error: null });
  await refreshFitRecords();
  await refreshAllFitRecords();
  return data.fit_uuid;
}

export async function deleteFitRecord(fitUuid) {
  const res = await fetch(`${API_BASE}/fit-records/${fitUuid}`, {
    method: 'DELETE', headers: authHeaders(),
  });
  if (res.status === 401) { handleUnauthorized(); return false; }
  if (res.ok || res.status === 404) {
    setState({
      fits: state.fits.filter((f) => f.fit_uuid !== fitUuid),
      allFits: state.allFits.filter((f) => f.fit_uuid !== fitUuid),
      selectedFitUuids: state.selectedFitUuids.filter((u) => u !== fitUuid),
    });
  }
  return res.ok;
}

export async function setActiveFit(fitUuid) {
  const res = await fetch(`${API_BASE}/fit-records/${fitUuid}/set-active`, {
    method: 'POST', headers: authHeaders(),
  });
  if (res.status === 401) { handleUnauthorized(); return false; }
  if (res.ok) {
    setState({
      fits: state.fits.map((f) => ({ ...f, is_active: f.fit_uuid === fitUuid })),
      allFits: state.allFits.map((f) => ({ ...f, is_active: f.fit_uuid === fitUuid })),
    });
  }
  return res.ok;
}

// Full detail for one fit (dataset_selection, fitted_parameters,
// optimizer_config, metrics, ...) — the summary rows in state.fits only
// carry the columns list_fit_records selects (label/model/r2/status/etc),
// deliberately light for the list view. Used by the Dashboard's "View"
// panel and anywhere else that needs the complete saved record.
export async function fetchFitDetail(fitUuid) {
  const res = await fetch(`${API_BASE}/fit-records/${fitUuid}`, { headers: authHeaders() });
  if (res.status === 401) { handleUnauthorized(); return null; }
  if (!res.ok) return null;
  return res.json();
}

// ── comparison selection + compare call ─────────────────────────────────

export function toggleFitSelected(fitUuid) {
  setState((s) => {
    const already = s.selectedFitUuids.includes(fitUuid);
    if (already) {
      return { selectedFitUuids: s.selectedFitUuids.filter((u) => u !== fitUuid) };
    }
    if (s.selectedFitUuids.length >= 4) return {}; // MAX_COMPARE, mirrors server cap
    return { selectedFitUuids: [...s.selectedFitUuids, fitUuid] };
  });
}

export function clearSelection() {
  setState({ selectedFitUuids: [], compareResult: null });
}

export async function runCompare() {
  // BUG FIX (Compare Fits — single custom-system fit produced no
  // comparison graph, client feedback 2-Sep call). See fit_records_api.py's
  // matching change on /fit-records/compare and CompareFitsPage.jsx's
  // Compare Selected button — all three layers (this guard, the button's
  // disabled state, the backend's own floor) enforced the same 2-fit
  // minimum and all three needed to move together, or lowering just one
  // would still silently no-op or 400 at whichever layer was missed.
  if (state.selectedFitUuids.length < 1) {
    setState({ error: 'select at least 1 fit to compare' });
    return null;
  }
  setState({ loading: true, error: null });
  try {
    const res = await fetch(`${API_BASE}/fit-records/compare`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ fit_uuids: state.selectedFitUuids }),
    });
    if (res.status === 401) { handleUnauthorized(); return null; }
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `compare failed: ${res.status}`);
    }
    const compareResult = await res.json();
    setState({ compareResult, loading: false });
    return compareResult;
  } catch (e) {
    setState({ loading: false, error: e.message });
    return null;
  }
}

export function exportSelectedUrl() {
  if (state.selectedFitUuids.length === 0) return null;
  const qs = encodeURIComponent(state.selectedFitUuids.join(','));
  return `${API_BASE}/fit-records/export?fit_uuids=${qs}`;
}

// Export needs the auth header, so a plain <a href> won't carry the
// token — fetch as a blob and trigger the download manually instead.
export async function downloadSelectedExport() {
  const url = exportSelectedUrl();
  if (!url) return false;
  const res = await fetch(url, { headers: authHeaders() });
  if (res.status === 401) { handleUnauthorized(); return false; }
  if (!res.ok) return false;
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = 'fit_comparison_export.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
  return true;
}

// ── session autosave (last input + last run, login-specific) ───────────

let _sessionSaveTimer = null;

// Debounced — call on every form-field change; only actually PUTs 800ms
// after the user stops typing, so we're not firing a request per keystroke.
export function autosaveFormState(formState) {
  lastFormState = formState; // cached for saveFitRecord's input_snapshot
  if (!state.reactionSystemId) return;
  if (_sessionSaveTimer) clearTimeout(_sessionSaveTimer);
  _sessionSaveTimer = setTimeout(() => {
    fetch(`${API_BASE}/session-state`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ system_uuid: state.reactionSystemId, form_state: formState }),
    }).catch(() => { /* best-effort autosave — don't surface network errors to the user */ });
  }, 800);
}

// Called once, right after a run completes — not debounced, this one matters.
export function autosaveLastRun(result, meta) {
  if (!state.reactionSystemId) return;
  fetch(`${API_BASE}/session-state`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({
      system_uuid: state.reactionSystemId,
      last_run_result: result,
      last_run_meta: meta,
    }),
  }).catch(() => {});
}

// Reset support: wipe the saved session (form fields + last run) of the system
// currently held by this store, right now, instead of waiting for the two
// stacked 800ms autosave debounces (useInputSession's, then
// autosaveFormState's) to overwrite it with blank values. Without this a
// refresh shortly after Reset -- or any later page load that lands on this
// system again -- would restore the PREVIOUS fit run's selections.
// The backend upsert COALESCEs each column, so an explicit JSON null is
// stored as 'null' and read back as null (no form_state / no last run).
// Saved fits (fit_records) are NOT touched.
export function clearSessionState() {
  // Drop any pending debounced autosave of the OLD form so it can't land
  // after this clear and put the old values back.
  if (_sessionSaveTimer) { clearTimeout(_sessionSaveTimer); _sessionSaveTimer = null; }
  lastFormState = null;
  if (!state.reactionSystemId) return Promise.resolve();
  return fetch(`${API_BASE}/session-state`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({
      system_uuid: state.reactionSystemId,
      form_state: null,
      last_run_result: null,
      last_run_meta: null,
    }),
  }).catch(() => { /* best-effort, same as the other autosave calls */ });
}

export async function loadSessionState(reactionSystemId) {
  const res = await fetch(
    `${API_BASE}/session-state?reaction_system_id=${encodeURIComponent(reactionSystemId)}`,
    { headers: authHeaders() }
  );
  if (res.status === 401) { handleUnauthorized(); return null; }
  if (!res.ok) return null;
  return res.json(); // { form_state, last_run_result, last_run_meta, updated_at }
}